import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { Button } from "./ui/button";

/**
 * PWA Install prompt.
 * - Listens to beforeinstallprompt (Chrome/Edge/Android)
 * - Detects iOS Safari and shows manual instructions (iOS doesn't fire the event)
 * - Hides after install or dismissal (sessionStorage)
 */
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosShow, setIosShow] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("pwa-dismissed")) return;

    // Already installed?
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // iOS detection (Safari doesn't fire beforeinstallprompt)
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIOS) {
      setIosShow(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      setIosShow(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem("pwa-dismissed", "1");
    setShow(false);
    setIosShow(false);
  };

  if (!show && !iosShow) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-fade-in-up" data-testid="pwa-install-banner">
      <div className="bg-slate-900 text-white rounded-sm shadow-2xl border border-slate-700 overflow-hidden">
        <div className="p-4 flex items-start gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#B89C65] flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif-display text-base font-medium mb-1">Installer l'app</p>
            <p className="text-xs text-slate-300 leading-relaxed">
              Ajoutez "Un Avocat en Or" à votre écran d'accueil pour un accès 1-clic en cas d'urgence.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-white -mt-1 -mr-1"
            data-testid="pwa-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {show && (
          <div className="px-4 pb-4">
            <Button
              onClick={handleInstall}
              className="w-full rounded-sm bg-[#B89C65] hover:bg-[#A38A58] text-white h-10"
              data-testid="pwa-install-button"
            >
              <Download className="h-4 w-4 mr-2" /> Installer maintenant
            </Button>
          </div>
        )}
        {iosShow && !iosOpen && (
          <div className="px-4 pb-4">
            <Button
              onClick={() => setIosOpen(true)}
              variant="outline"
              className="w-full rounded-sm border-slate-700 bg-transparent text-white hover:bg-slate-800 h-10"
              data-testid="pwa-ios-instructions"
            >
              Voir les instructions (iOS)
            </Button>
          </div>
        )}
        {iosShow && iosOpen && (
          <div className="px-4 pb-4 text-xs text-slate-300 leading-relaxed border-t border-slate-700 pt-3 space-y-2" data-testid="pwa-ios-steps">
            <p><strong className="text-white">1.</strong> Touchez l'icône <span className="inline-block bg-slate-700 px-1.5 py-0.5 rounded text-[10px]">Partager ⬆️</span> en bas de Safari</p>
            <p><strong className="text-white">2.</strong> Faites défiler et touchez <strong>"Sur l'écran d'accueil"</strong></p>
            <p><strong className="text-white">3.</strong> Touchez <strong>"Ajouter"</strong> en haut à droite</p>
          </div>
        )}
      </div>
    </div>
  );
}
